import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { persistLanguage, SUPPORTED_LANGUAGES } from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  
  // Cache to prevent excessive API calls
  const profileCache = React.useRef({
    data: null,
    timestamp: null,
    userId: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Fetch user profile from backend API with caching
  const fetchUserProfile = async (session) => {
    try {
      const now = Date.now();
      const userId = session?.user?.id;
      
      // Return cached profile if valid
      if (
        profileCache.current.data && 
        profileCache.current.userId === userId &&
        profileCache.current.timestamp &&
        (now - profileCache.current.timestamp) < CACHE_DURATION
      ) {
        console.log('Using cached profile');
        return profileCache.current.data;
      }

      console.log('Fetching fresh profile from API');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        console.error('Error fetching user profile:', response.status);
        return profileCache.current.data; // Return cached data on error if available
      }

      const result = await response.json();
      
      if (result.success) {
        // Update cache
        profileCache.current = {
          data: result.data,
          timestamp: now,
          userId: userId
        };
        return result.data;
      }

      return profileCache.current.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return profileCache.current.data; // Return cached data on error if available
    }
  };

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session) {
        fetchUserProfile(session).then(profile => {
          if (isMounted) {
            setUserProfile(profile);
            setLoading(false);
            // Auto-apply the user's saved language across every page they visit.
            const saved = String(profile?.preferredLanguage || '').toLowerCase();
            if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
              persistLanguage(saved);
            }
          }
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes with debouncing
    let debounceTimer = null;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      // Debounce auth state changes
      debounceTimer = setTimeout(async () => {
        if (!isMounted) return;
        
        console.log('Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session && event !== 'TOKEN_REFRESHED') {
          // Only fetch profile on actual auth changes, not token refresh
          const profile = await fetchUserProfile(session);
          if (isMounted) {
            setUserProfile(profile);
            // Auto-apply the user's saved language on login / auth switch.
            const saved = String(profile?.preferredLanguage || '').toLowerCase();
            if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
              persistLanguage(saved);
            }
          }
        } else if (!session) {
          setUserProfile(null);
          // Clear cache on logout
          profileCache.current = { data: null, timestamp: null, userId: null };
        }
        
        setLoading(false);
      }, 300); // 300ms debounce
    });

    return () => {
      isMounted = false;
      if (debounceTimer) clearTimeout(debounceTimer);
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Fetch user profile
      if (data.session) {
        const profile = await fetchUserProfile(data.session);
        setUserProfile(profile);

        // Fire-and-forget audit event (server filters non-recruiter roles)
        try {
          const userId = profile?.id || data.session?.user?.id;
          if (userId) {
            const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
            fetch(`${backendBase}/api/activity`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'login_success', actorUserId: userId }),
            }).catch(() => {});
          }
        } catch (_e) { /* swallow */ }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      setUserProfile(null);
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  };

  // Force refresh user profile (invalidate cache)
  const refreshProfile = async () => {
    if (session) {
      profileCache.current = { data: null, timestamp: null, userId: null };
      const profile = await fetchUserProfile(session);
      if (profile) setUserProfile(profile);
    }
  };

  const value = {
    user,
    session,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
